import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BACKEND_URL, fmt, calcPricing } from '../utils';
import { TermsModal } from './TermsModal';
import { PrivacyModal } from './PrivacyModal';
import { useLang } from '../i18n';
import { renderNote } from './specialHoursNote';

let _stripeInstance = null;
let _stripeLoading = null;

function WhatsIncluded() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="whats-included" style={{ margin: '0 0 12px' }}>
      <button
        data-testid="whats-included-toggle"
        onClick={() => setOpen(p => !p)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '12px 14px', background: '#FAFAF8', border: '1px solid #e8e4df',
          borderRadius: open ? '8px 8px 0 0' : 8, cursor: 'pointer', fontFamily: 'Jost, sans-serif',
          fontSize: 13, fontWeight: 600, color: 'var(--bark, #1C1814)',
        }}
      >
        <span>{t('dayPassTitle')}</span>
        <span style={{ fontSize: 11, color: '#8B7E74' }}>{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div data-testid="whats-included-content" style={{
          padding: '12px 16px', background: '#FAFAF8', border: '1px solid #e8e4df',
          borderTop: 'none', borderRadius: '0 0 8px 8px', fontSize: 12, lineHeight: 1.8,
          color: 'var(--bark)', fontFamily: 'Jost, sans-serif',
        }}>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>{t('thermalExperience')}</div>
          <ul style={{ margin: '0 0 8px', paddingLeft: 18 }}>
            <li>{t('incThermal')}</li>
            <li>{t('incSauna')}</li>
            <li>{t('incTea')}</li>
            <li>{t('incGym')}</li>
            <li>{t('incTowels')}</li>
          </ul>
          <div style={{ fontSize: 11, color: '#8B7E74', fontStyle: 'italic' }}>
            {t('incNote')}
          </div>
        </div>
      )}
    </div>
  );
}

function getStripe(publishableKey) {
  if (_stripeInstance) return Promise.resolve(_stripeInstance);
  if (_stripeLoading) return _stripeLoading;
  _stripeLoading = new Promise((resolve) => {
    const init = () => { _stripeInstance = window.Stripe(publishableKey); resolve(_stripeInstance); };
    if (window.Stripe) { init(); return; }
    const s = document.createElement('script');
    s.src = 'https://js.stripe.com/v3/';
    s.onload = init;
    document.head.appendChild(s);
  });
  return _stripeLoading;
}

export function CheckoutModal({ slot, guests: initialGuests, config, displayRemaining, isPopular, urgencyConfig = {}, onClose, onComplete, trackEvent, preselectedBundleId = null, onGuestsChange }) {
  const { t, lang } = useLang();
  const [guests, setGuests] = useState(initialGuests);
  const activeBundles = useMemo(
    () => (config.bundles || []).filter(b => b.active && b.passCount > 0),
    [config.bundles]
  );
  // Only bundles whose passCount is <= the selected guest count qualify.
  // Sort by best discount first; use passCount as tiebreaker so the most
  // specific threshold wins when discounts are equal.
  const qualifyingBundles = useMemo(
    () => activeBundles
      .filter(b => b.passCount <= guests)
      .sort((a, b) => b.discountPercent - a.discountPercent || b.passCount - a.passCount),
    [activeBundles, guests]
  );
  const [selectedBundle, setSelectedBundle] = useState(() => {
    if (preselectedBundleId) {
      return activeBundles.find(b => b.id === preselectedBundleId) || null;
    }
    // Auto-select the best qualifying bundle (highest passCount <= guests)
    return qualifyingBundles[0] || null;
  });
  const [promoInput, setPromoInput] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [promoNotice, setPromoNotice] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [step, setStep] = useState('form');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [bookingRef, setBookingRef] = useState('');
  const [bookingError, setBookingError] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [paying, setPaying] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [bookingToken, setBookingToken] = useState('');
  const [termsError, setTermsError] = useState('');

  const [stripeObj, setStripeObj] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);
  const paymentRef = useRef(null);
  const payingRef = useRef(false);

  const notifyParent = useCallback((n) => { if (onGuestsChange) onGuestsChange(n); }, [onGuestsChange]);

  const handleGuestsChange = useCallback((n) => {
    const clamped = Math.min(Math.max(n, 1), displayRemaining);
    setGuests(clamped);
    notifyParent(clamped);
    // Re-evaluar mejor bundle para la nueva cantidad
    const best = activeBundles
      .filter(b => b.passCount <= clamped)
      .sort((a, b) => b.discountPercent - a.discountPercent || b.passCount - a.passCount)[0] || null;
    setSelectedBundle(best);
    if (best) { setAppliedPromo(null); setPromoInput(''); setPromoError(''); setPromoNotice(''); }
    else if (appliedPromo && clamped !== guests) {
      // The code was validated for the old party size, and its per-slot guest cap is
      // counted against it. Re-applying is one click and cannot show a stale price.
      setAppliedPromo(null);
      setPromoError('');
      setPromoNotice('Guest count changed — apply your code again.');
    }
  }, [activeBundles, displayRemaining, notifyParent, appliedPromo, guests]);

  const handleBundleSelect = useCallback((bundle) => {
    if (bundle === null) {
      setSelectedBundle(null);
      const newGuests = 1;
      setGuests(newGuests);
      notifyParent(newGuests);
    } else {
      setSelectedBundle(bundle);
      setAppliedPromo(null); setPromoInput(''); setPromoError('');
      if (guests < bundle.passCount) {
        const newGuests = Math.min(bundle.passCount, displayRemaining);
        setGuests(newGuests);
        notifyParent(newGuests);
      }
    }
  }, [guests, displayRemaining, notifyParent]);

  // Bundle discount applies to all selected guests (not capped to bundle's passCount)
  const effectiveGuests = guests;
  // Fee and tax are always on the full subtotal; discount is subtracted from gross (matches backend)
  const base = calcPricing(slot.price, effectiveGuests, config.taxRate, config.serviceFeeRate);
  let bundleDiscount = 0;
  let promoDiscount = 0;
  if (selectedBundle) {
    bundleDiscount = Math.round(base.subtotal * (selectedBundle.discountPercent / 100) * 100) / 100;
  } else if (appliedPromo) {
    promoDiscount = appliedPromo.type === 'percent'
      ? Math.round(base.subtotal * (appliedPromo.discount / 100) * 100) / 100
      : appliedPromo.discount;
  }
  const finalTotal = Math.max(base.total - bundleDiscount - promoDiscount, 0);
  const amountCents = Math.round(finalTotal * 100);

  useEffect(() => {
    setAppliedPromo(null);
    setPromoError('');
    setPromoNotice('');
  }, [slot.id]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  useEffect(() => {
    if (!config.stripePublishableKey) return;
    getStripe(config.stripePublishableKey).then((s) => setStripeObj(s));
  }, [config.stripePublishableKey]);

  useEffect(() => {
    if (!stripeObj || !paymentRef.current || elements) return;
    const elems = stripeObj.elements({
      mode: 'payment',
      amount: amountCents > 0 ? amountCents : 100,
      currency: 'usd',
      paymentMethodTypes: ['card'],
      // Must match the PaymentIntent created by /api/create-payment-intent — Stripe refuses
      // to confirm when the element and the intent disagree on setup_future_usage.
      setupFutureUsage: 'off_session',
      appearance: { theme: 'stripe' },
    });
    // Stripe would print its own card-on-file consent sentence above ours because of
    // setup_future_usage; ours is the one the client approved, so suppress Stripe's.
    const pe = elems.create('payment', { terms: { card: 'never' } });
    pe.mount(paymentRef.current);
    pe.on('ready', () => setPaymentReady(true));
    setElements(elems);
  }, [stripeObj, amountCents, elements]);

  useEffect(() => {
    if (!elements || amountCents <= 0) return;
    elements.update({ amount: amountCents });
  }, [elements, amountCents]);

  const applyPromo = useCallback(async () => {
    const code = promoInput.toUpperCase().trim();
    if (!code) return;
    setPromoError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/validate-promo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, email: email.trim(), slotId: slot.id, guests: effectiveGuests }),
      });
      const data = await res.json();
      if (data.valid && data.promo) {
        setAppliedPromo(data.promo);
        setPromoError('');
        setPromoNotice('');
      } else {
        setAppliedPromo(null);
        setPromoNotice('');
        setPromoError(data.error || 'Invalid promo code');
      }
    } catch {
      setAppliedPromo(null);
      setPromoNotice('');
      setPromoError('Could not validate promo code');
    }
  }, [promoInput, email, slot.id, effectiveGuests]);

  const handlePay = useCallback(async () => {
    if (payingRef.current) return;
    payingRef.current = true;

    if (!name.trim() || !email.trim() || !phone.replace(/\D/g, '') || !termsAgreed) {
      setAttempted(true);
      if (!termsAgreed) setTermsError('Please agree to the Terms & Conditions to continue');
      payingRef.current = false;
      return;
    }
    if (!stripeObj || !elements || !paymentReady) {
      payingRef.current = false;
      return;
    }
    setAttempted(false);
    setTermsError('');
    setPaying(true);
    setBookingError('');

    // Step 1: Validate card form (no charge yet)
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setPaying(false);
      setBookingError(submitError.message);
      payingRef.current = false;
      return;
    }

    // Step 2: Reserve slot atomically — slot is held before any charge
    let reserveData;
    try {
      const reserveRes = await fetch(`${BACKEND_URL}/api/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotId: slot.id,
          guests: effectiveGuests,
          name: name.trim(),
          email: email.trim(),
          phone: phone.replace(/\D/g, '') || '',
          promoCode: selectedBundle ? '' : (appliedPromo ? appliedPromo.code : ''),
          bundleId: selectedBundle ? selectedBundle.id : '',
        }),
      });
      reserveData = await reserveRes.json();
      if (!reserveRes.ok) {
        setPaying(false);
        setBookingError(reserveData.detail || t('slotNoLongerAvailable'));
        payingRef.current = false;
        return;
      }
    } catch (e) {
      setPaying(false);
      setBookingError(t('networkError'));
      payingRef.current = false;
      return;
    }

    const { ref, releaseToken, serverTotal } = reserveData;
    setBookingRef(ref);

    // Helper to release the reservation on any failure
    const release = () => fetch(`${BACKEND_URL}/api/release/${ref}/${releaseToken}`, { method: 'POST' }).catch(() => {});

    // Step 3: Create PaymentIntent using server-calculated total
    const chargeAmountCents = Math.round((serverTotal || finalTotal) * 100);
    let piData;
    try {
      const piRes = await fetch(`${BACKEND_URL}/api/create-payment-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount_cents: chargeAmountCents,
          booking_ref: ref,
          customer_email: email.trim(),
          customer_name: name.trim(),
          description: `Las Ruinas Day Pass - ${slot.day}, ${slot.date} ${slot.time}`,
        }),
      });
      piData = await piRes.json();
      if (!piData.client_secret) {
        await release();
        setPaying(false);
        setBookingError(piData.detail || t('failedPayment'));
        payingRef.current = false;
        return;
      }
    } catch (e) {
      await release();
      setPaying(false);
      setBookingError(t('networkError'));
      payingRef.current = false;
      return;
    }

    // Step 4: Charge the card via Stripe
    const { error, paymentIntent } = await stripeObj.confirmPayment({
      elements,
      clientSecret: piData.client_secret,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    if (error) {
      await release();
      setPaying(false);
      setBookingError(error.message);
      payingRef.current = false;
      return;
    }

    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Step 5: Confirm reservation as paid in the backend
      try {
        const confirmRes = await fetch(`${BACKEND_URL}/api/confirm-reservation/${ref}/${releaseToken}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentIntentId: paymentIntent.id,
            termsAgreed: true,
            termsAgreedAt: new Date().toISOString(),
            termsVersion: '1.0',
          }),
        });
        const confirmData = await confirmRes.json();
        // Even if this call fails, the Stripe webhook will confirm the booking.
        if (confirmData.rescheduleToken) setBookingToken(confirmData.rescheduleToken);
      } catch (e) {
        // Non-fatal: webhook will handle confirmation
      }

      try { await onComplete(); } catch (e) {}
      if (trackEvent) trackEvent('purchase', { value: serverTotal || finalTotal, ref });
      setStep('success');
      setEmailStatus('sent');
      payingRef.current = false;
    }
  }, [stripeObj, elements, paymentReady, slot, guests, effectiveGuests, selectedBundle, name, email, phone, finalTotal, appliedPromo, config, onComplete, termsAgreed]);

  if (step === 'success') {
    const confirmationUrl = bookingToken ? `/booking/${bookingRef}/${bookingToken}` : '';
    return (
      <div className="lr-overlay" data-testid="checkout-success">
        <div className="lr-modal">
          <button className="close-btn" data-testid="close-success" onClick={onClose}>&times;</button>
          <div className="lr-success">
            <div className="sparkle">{'\u2728'}</div>
            <h3>{t('youreConfirmed')}</h3>
            <p style={{color:'var(--bark)',fontSize:13,marginBottom:12}}>{t('dayPassBooked')}</p>
            <div className="detail">
              {slot.day}, {slot.date}<br/>
              {slot.time} &middot; {guests} {guests > 1 ? t('guests') : t('guest')}<br/>
              {fmt(finalTotal)}
            </div>
            <div className="ref-badge" data-testid="booking-ref">{bookingRef}</div>
            <div className={`lr-email-status ${emailStatus}`} data-testid="email-status">
              {emailStatus === 'sending' && <><span className="spinner"></span> {t('sendingEmail')}</>}
              {emailStatus === 'sent' && <>{'\u2713'} {t('emailSent')}</>}
              {emailStatus === 'failed' && <>{'\u26A0'} {t('emailFailed')}</>}
              {emailStatus === 'idle' && null}
            </div>
            {confirmationUrl && (
              <a href={confirmationUrl} className="lr-view-booking-btn" data-testid="view-my-booking-btn">
                {t('viewMyBooking')}
              </a>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
  <>
    <div className="lr-overlay" data-testid="checkout-modal">
      <div className="lr-modal" style={{ position: 'relative' }}>
        {paying && <div style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'wait', borderRadius: 'inherit' }} />}
        <button className="close-btn" data-testid="close-checkout" onClick={onClose} disabled={paying}>&times;</button>
        <h3>{t('checkout')}</h3>
        <div data-testid="checkout-slot-info" style={{
          textAlign: 'center', fontSize: 13, color: '#4A4438',
          marginBottom: 10, fontWeight: 500, background: '#F5F0E8',
          padding: '8px 12px', borderRadius: 6,
        }}>
          {slot.day}, {slot.date} &middot; {slot.time}
        </div>
        {(() => {
          const list = Array.isArray(slot.specialHours) ? slot.specialHours : [];
          if (!list.length) return null;
          const blocks = list.map(sh => {
            const ty = (config.specialHourTypes || []).find(t => t.id === sh.typeId);
            const note = ty && (ty.note?.[lang] || ty.note?.en);
            return note ? { id: sh.ruleId || sh.typeId, note } : null;
          }).filter(Boolean);
          if (!blocks.length) return null;
          return (
            <div data-testid="checkout-special-hours-note" style={{ marginBottom: 10, display: 'grid', gap: 8 }}>
              {blocks.map(b => (
                <div key={b.id} style={{
                  fontSize: 12, lineHeight: 1.6, color: 'var(--umber)',
                  background: '#FFF8E1', border: '1px solid #F0D98C', borderRadius: 6,
                  padding: '10px 12px',
                }}>
                  {renderNote(b.note)}
                </div>
              ))}
            </div>
          );
        })()}
        {(() => {
          const showCheckoutUrgency = urgencyConfig.checkoutUrgencyEnabled !== false;
          const lowThresh = urgencyConfig.urgencyLowThreshold ?? 4;
          const critThresh = urgencyConfig.urgencyCriticalThreshold ?? 2;
          const lowMsg = urgencyConfig.checkoutLowMessage || 'Only {{spots}} spots left at this time';
          const critMsg = urgencyConfig.checkoutCriticalMessage || 'Only {{spots}} spots left \u2014 book now';
          const lastMsg = urgencyConfig.checkoutLastSpotMessage || 'This is the last available spot';
          const showPopular = urgencyConfig.popularEnabled !== false;

          if (showCheckoutUrgency && displayRemaining > 0 && displayRemaining <= lowThresh) {
            let msg = '';
            if (displayRemaining === 1) msg = lastMsg;
            else if (displayRemaining <= critThresh) msg = critMsg.replace('{{spots}}', String(displayRemaining));
            else msg = lowMsg.replace('{{spots}}', String(displayRemaining));
            return <div className="lr-urgency-text" data-testid="checkout-urgency">{'\u26A1'} {msg}</div>;
          }
          if (displayRemaining > lowThresh && showPopular && isPopular) {
            return <div className="lr-popular-text" data-testid="checkout-popular">{'\uD83D\uDD25'} One of our most popular time slots</div>;
          }
          return null;
        })()}

        {bookingError && <div className="lr-promo-error" data-testid="booking-error" style={{marginBottom:12}}>{bookingError}</div>}

        {/* What's Included */}
        <WhatsIncluded />

        {/* Selector de guests */}
        <div className="lr-section" data-testid="checkout-guests-section">
          <div className="lr-section-title">{t('guests')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              data-testid="checkout-guests-minus"
              disabled={guests <= 1}
              onClick={() => handleGuestsChange(guests - 1)}
              style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e8e4df', background: 'white', fontSize: 18, cursor: guests <= 1 ? 'not-allowed' : 'pointer', opacity: guests <= 1 ? 0.4 : 1 }}
            >−</button>
            <span data-testid="checkout-guests-count" style={{ fontWeight: 600, fontSize: 16, minWidth: 24, textAlign: 'center' }}>{guests}</span>
            <button
              data-testid="checkout-guests-plus"
              disabled={guests >= displayRemaining}
              onClick={() => handleGuestsChange(guests + 1)}
              style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid #e8e4df', background: 'white', fontSize: 18, cursor: guests >= displayRemaining ? 'not-allowed' : 'pointer', opacity: guests >= displayRemaining ? 0.4 : 1 }}
            >+</button>
          </div>
        </div>

        {/* Bundle Offers */}
        {activeBundles.length > 0 && (
          <div className="lr-section" data-testid="bundle-offers">
            <div className="lr-section-title">{t('bundleOffers')}</div>
            <div style={{display:'flex', flexDirection:'column', gap:6}}>
              <label style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', border:'1px solid #e8e4df', borderRadius:8, cursor:'pointer', background: !selectedBundle ? '#FAFAF8' : 'white'}}>
                <input type="radio" name="lr-bundle" checked={!selectedBundle} onChange={() => handleBundleSelect(null)} data-testid="bundle-none" />
                <span style={{fontSize:13}}>{t('noBundleSinglePass')}</span>
              </label>
              {activeBundles
                .sort((a, b) => a.passCount - b.passCount)
                .map(b => {
                  const qualifies = b.passCount <= guests;
                  const fullSub = slot.price * guests;
                  const disc = fullSub * (b.discountPercent / 100);
                  const preview = fmt(fullSub - disc);
                  const isSelected = selectedBundle?.id === b.id;
                  return (
                    <label key={b.id}
                      style={{display:'flex', alignItems:'center', gap:10, padding:'10px 12px', border:`1px solid ${isSelected ? 'var(--bark,#1C1814)' : '#e8e4df'}`, borderRadius:8, cursor:'pointer', background: isSelected ? '#FAFAF8' : 'white'}}
                      data-testid={`bundle-option-${b.id}`}>
                      <input type="radio" name="lr-bundle" checked={isSelected}
                        onChange={() => handleBundleSelect(b)} />
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600, fontSize:13}}>{b.name} &mdash; {b.passCount}+ {t('guests')}</div>
                        {qualifies ? (
                          <div style={{fontSize:11, color:'#8B7E74'}}>{t('bundleSaveX', { pct: b.discountPercent })} on all {guests} {guests !== 1 ? t('guests') : t('guest')} &mdash; {preview} <s style={{color:'#bbb'}}>{fmt(fullSub)}</s> (pre-tax)</div>
                        ) : (
                          <div style={{fontSize:11, color:'#C8A96E'}}>Selecting this will set guests to {b.passCount}</div>
                        )}
                      </div>
                    </label>
                  );
                })}
            </div>
          </div>
        )}

        <div className="lr-section">
          <div className="lr-section-title">{t('orderSummary')}</div>
          <div className="lr-summary-row"><span>{t('ticketLabel')} &times; {effectiveGuests}</span><span>{fmt(base.subtotal)}</span></div>
          <div className="lr-summary-row"><span>{t('serviceFee')} ({config.serviceFeeRate}%)</span><span>{fmt(base.fee)}</span></div>
          <div className="lr-summary-row"><span>{t('tax')} ({config.taxRate}%)</span><span>{fmt(base.tax)}</span></div>
          {bundleDiscount > 0 && <div className="lr-summary-row"><span>{t('bundle')} ({selectedBundle.name})</span><span className="discount">-{fmt(bundleDiscount)}</span></div>}
          {promoDiscount > 0 && <div className="lr-summary-row"><span>{t('promo')} ({appliedPromo.code})</span><span className="discount">-{fmt(promoDiscount)}</span></div>}
          <div className="lr-summary-row total"><span>{t('total')}</span><span>{fmt(finalTotal)}</span></div>
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('contact')}</div>
          <div className="lr-field">
            <label className="lr-label">{t('fullName')}</label>
            <input className="lr-input" data-testid="contact-name" value={name} onChange={e => { setName(e.target.value); setAttempted(false); }} placeholder="Maria Santos" style={attempted && !name.trim() ? {borderColor:'var(--red)'} : {}} />
            {attempted && !name.trim() && <div className="lr-promo-error" data-testid="name-error" style={{marginTop:4}}>{t('nameRequired')}</div>}
          </div>
          <div className="lr-field">
            <label className="lr-label">{t('email')}</label>
            <input className="lr-input" data-testid="contact-email" type="email" value={email} onChange={e => { setEmail(e.target.value); setAttempted(false); }} placeholder="maria@example.com" style={attempted && !email.trim() ? {borderColor:'var(--red)'} : {}} />
            {attempted && !email.trim() && <div className="lr-promo-error" data-testid="email-error" style={{marginTop:4}}>{t('emailRequired')}</div>}
          </div>
          <div className="lr-field">
            <label className="lr-label">{t('phoneNumber')}</label>
            <input className="lr-input" data-testid="contact-phone" type="tel" value={phone} onChange={e => {
              const digits = e.target.value.replace(/\D/g, '');
              let formatted = '';
              if (digits.length === 0) formatted = '';
              else if (digits.length <= 1) formatted = '+' + digits;
              else if (digits.length <= 4) formatted = '+' + digits[0] + ' (' + digits.slice(1);
              else if (digits.length <= 7) formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4);
              else formatted = '+' + digits[0] + ' (' + digits.slice(1, 4) + ') ' + digits.slice(4, 7) + '-' + digits.slice(7, 11);
              setPhone(formatted);
              setAttempted(false);
            }} placeholder="+1 (787) 000-0000" style={attempted && !phone.replace(/\D/g, '') ? {borderColor:'var(--red)'} : {}} />
            {attempted && !phone.replace(/\D/g, '') && <div className="lr-promo-error" data-testid="phone-error" style={{marginTop:4}}>{t('phoneRequired')}</div>}
          </div>
        </div>

        <div className="lr-section">
          <div className="lr-terms-row" data-testid="terms-checkbox-row">
            <label className="lr-terms-label">
              <input type="checkbox" className="lr-terms-check" data-testid="terms-checkbox" checked={termsAgreed} onChange={e => { setTermsAgreed(e.target.checked); if (e.target.checked) setTermsError(''); }} />
              <span className="lr-terms-text">
                {name.trim() ? <>{t('iPersonAgree', { name: name.trim() })} </> : <>{t('iAgree')} </>}
                <button type="button" className="lr-terms-link" data-testid="terms-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}>{t('termsAndConditions')}</button>
                {' '}{t('andConnector')}{' '}
                <button type="button" className="lr-terms-link" data-testid="privacy-link" onClick={e => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}>{t('privacyPolicy')}</button>
              </span>
            </label>
          </div>
          {termsError && <div className="lr-promo-error" data-testid="terms-error" style={{marginTop:6}}>{termsError}</div>}
        </div>

        {/* Bundle Policy — shown only when a bundle is selected */}
        {selectedBundle && (
          <div data-testid="bundle-policy-box" style={{
            margin: '0 0 16px',
            padding: '14px 16px',
            background: '#FFF8E1',
            border: '1px solid #F0D98C',
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.7,
            color: 'var(--bark)',
            fontFamily: 'Jost, sans-serif',
          }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('bundlePolicyTitle')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('bundleSameDay')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('bundleNonCarryover')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('bundleNonRefundable')}</div>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('bundleNonTransferable')}</div>
          </div>
        )}

        {/* Cancellation Policy */}
        <div data-testid="cancellation-policy-box" style={{
          margin: '0 0 16px',
          padding: '14px 16px',
          background: '#FAFAF8',
          border: '1px solid #e8e4df',
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.7,
          color: 'var(--bark)',
          fontFamily: 'Jost, sans-serif',
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('cancellationPolicy')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#2D7A4F' }}>&#10003;</span> {t('freeCancellation')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('noRefund24h')}</div>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 2 }}><span style={{ color: '#D4321C' }}>&#10007;</span> {t('noRefundNoShow')}</div>
          <div style={{ fontSize: 11, color: '#8B7E74', marginTop: 6 }}>{t('stripeNonRefundable')}</div>
        </div>

        {/* Additional Costs */}
        <div data-testid="additional-costs-box" style={{
          margin: '0 0 16px',
          padding: '14px 16px',
          background: '#FAFAF8',
          border: '1px solid #e8e4df',
          borderRadius: 8,
          fontSize: 12,
          lineHeight: 1.7,
          color: 'var(--bark)',
          fontFamily: 'Jost, sans-serif',
        }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>{t('additionalCostsTitle')}</div>
          <div>{t('lockerNote')}</div>
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('promoCode')}</div>
          {selectedBundle ? (
            <div style={{fontSize:12, color:'#8B7E74', padding:'8px 0'}} data-testid="promo-disabled-bundle">
              Promo codes cannot be combined with bundle offers.
            </div>
          ) : (
            <>
              <div className="lr-promo-row">
                <input className="lr-input" data-testid="promo-input" value={promoInput}
                  onChange={e => { setPromoInput(e.target.value.toUpperCase()); setPromoError(''); setPromoNotice(''); }} placeholder={t('enterCode')} />
                <button className="lr-btn-sm lr-btn-accent" data-testid="apply-promo" onClick={applyPromo}>{t('apply')}</button>
              </div>
              {appliedPromo && <div className="lr-promo-success" data-testid="promo-success">{'\u2713'} {appliedPromo.label} &mdash; {appliedPromo.type === 'percent' ? `${appliedPromo.discount}% ${t('off')}` : `${fmt(appliedPromo.discount)} ${t('off')}`}</div>}
              {appliedPromo && appliedPromo.validity && <div style={{fontSize:11,color:'#8B7E74',marginTop:2}} data-testid="promo-validity">{appliedPromo.validity}</div>}
              {promoNotice && <div style={{fontSize:11,color:'#8B7E74',marginTop:2}} data-testid="promo-notice">{promoNotice}</div>}
              {promoError && <div className="lr-promo-error" data-testid="promo-error">{promoError}</div>}
            </>
          )}
        </div>

        <div className="lr-section">
          <div className="lr-section-title">{t('payment')}</div>
          <div ref={paymentRef} data-testid="stripe-payment-element" style={{minHeight:100,marginBottom:4}}></div>
          <div className="lr-stripe-badge" data-testid="stripe-secured-badge">&#128274; {t('paymentsSecuredBy')}</div>
          <div data-testid="card-on-file-consent" style={{ fontSize: 11, color: '#8B7E74', lineHeight: 1.5, margin: '6px 0 8px' }}>{t('cardOnFileConsent')}</div>
          <button className="lr-pay-full primary" data-testid="pay-button" disabled={!paymentReady || paying || !termsAgreed} onClick={handlePay}>
            {paying ? t('processing') : `${t('pay')} ${fmt(finalTotal)}`}
          </button>
        </div>
      </div>
    </div>
    {showTermsModal && (
      <TermsModal
        onAgree={() => { setTermsAgreed(true); setTermsError(''); setShowTermsModal(false); }}
        onClose={() => setShowTermsModal(false)}
      />
    )}
    {showPrivacyModal && (
      <PrivacyModal
        onClose={() => setShowPrivacyModal(false)}
      />
    )}
  </>
  );
}
